<script setup lang="ts">
import axios from 'axios';
import { ref } from 'vue';
import ToolPageHead from '@/components/seo/ToolPageHead.vue';
import DropZone from '@/components/Tools/DropZone.vue';
import FileCard from '@/components/Tools/FileCard.vue';
import StatusMessage from '@/components/Tools/StatusMessage.vue';
import ToolLayout from '@/components/Tools/ToolLayout.vue';
import { Button } from '@/components/ui/button';
import { usePdfUploadLimit } from '@/composables/usePdfUploadLimit';
import { useToolPageSeoFromInertia } from '@/composables/useToolPageSeo';
import { useTranslations } from '@/composables/useTranslations';
import { dashboard } from '@/routes';
import { unlock as unlockPage } from '@/routes/tools/pdf';
import unlockTool from '@/routes/tools/pdf/unlock';

const { lineWithMax } = usePdfUploadLimit();
const { t } = useTranslations();

const seo = useToolPageSeoFromInertia({
    seoPrefix: 'tools.pdf.unlock.seo',
    fallbackTitleKey: 'tools.pdf.unlock.title',
    fallbackTitleLabel: 'Unlock PDF',
    t,
    homeRelativeUrl: dashboard.url(),
    selfRelativeUrl: unlockPage.url(),
});

defineOptions({
    layout: {
        breadcrumbs: [
            {
                translationKey: 'tools.nav.home',
                title: 'Home',
                href: dashboard.url(),
            },
            {
                translationKey: 'tools.pdf.unlock.title',
                title: 'Unlock PDF',
                href: unlockPage.url(),
            },
        ],
    },
});

type Status = 'idle' | 'loading' | 'success' | 'error';

const file = ref<File | null>(null);
const password = ref('');
const status = ref<Status>('idle');
const statusMessage = ref('');

function onFileSelected(files: File[]): void {
    file.value = files[0] ?? null;
    status.value = 'idle';
}

async function extractError(err: unknown): Promise<string> {
    const e = err as { response?: { data: Blob | object } };

    if (e.response?.data instanceof Blob) {
        try {
            const parsed = JSON.parse(await (e.response.data as Blob).text());
            const msgs = Object.values(parsed.errors ?? {}).flat();

            return (
                (msgs[0] as string) ||
                t('tools.pdf.ui.unlock_failed', 'Unlock failed.')
            );
        } catch {
            return t('tools.pdf.ui.unlock_failed', 'Unlock failed.');
        }
    }

    return t('tools.pdf.ui.unlock_failed', 'Unlock failed.');
}

async function unlock(): Promise<void> {
    if (!file.value || !password.value || status.value === 'loading') {
        return;
    }

    status.value = 'loading';
    statusMessage.value = t(
        'tools.pdf.ui.unlocking',
        'Removing password protection...',
    );

    const formData = new FormData();
    formData.append('file', file.value);
    formData.append('password', password.value);

    try {
        const response = await axios.post(unlockTool.submit.url(), formData, {
            responseType: 'blob',
        });

        const cd = (response.headers['content-disposition'] as string) ?? '';
        const match = cd.match(/filename="?([^"]+)"?/);
        const filename = match?.[1] ?? 'unlocked.pdf';

        const blobUrl = URL.createObjectURL(response.data as Blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = filename;
        a.click();
        URL.revokeObjectURL(blobUrl);

        status.value = 'success';
        statusMessage.value = t(
            'tools.pdf.ui.unlocked_success',
            'PDF unlocked successfully!',
        );
        file.value = null;
        password.value = '';
    } catch (err: unknown) {
        status.value = 'error';
        statusMessage.value = await extractError(err);
    }
}
</script>

<template>
    <ToolPageHead v-bind="seo" json-ld-head-key="ld-json-pdf-unlock" />

    <ToolLayout
        :title="t('tools.pdf.unlock.title', 'Unlock PDF')"
        :subtitle="t('tools.pdf.unlock.description', '')"
    >
        <div class="space-y-4">
            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <DropZone
                    accept="application/pdf"
                    :multiple="false"
                    :label="
                        t('tools.pdf.ui.drop_pdf', 'Click or drag a PDF here')
                    "
                    :sublabel="
                        lineWithMax(
                            t(
                                'tools.pdf.ui.password_protected_pdf',
                                'Password-protected PDF',
                            ),
                        )
                    "
                    @update:model-value="onFileSelected"
                />
                <div v-if="file" class="mt-3">
                    <FileCard
                        :file="file"
                        :show-drag-handle="false"
                        @remove="file = null"
                    />
                </div>
            </div>

            <div class="rounded-xl border border-border bg-card p-6 shadow-sm">
                <label
                    for="password"
                    class="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                    {{
                        t(
                            'tools.pdf.ui.unlock_pdf_password_label',
                            'PDF Password',
                        )
                    }}
                </label>
                <input
                    id="password"
                    v-model="password"
                    type="password"
                    :placeholder="
                        t(
                            'tools.pdf.ui.password_placeholder',
                            'Enter the PDF password',
                        )
                    "
                    class="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:border-brand-500 focus:ring-2 focus:ring-brand-500 focus:outline-none dark:border-border dark:bg-muted dark:text-foreground"
                    @keydown.enter="unlock"
                />
                <p class="mt-2 text-xs text-gray-400 dark:text-gray-500">
                    {{ t('tools.pdf.ui.unlock_hint', '') }}
                </p>
            </div>

            <Button
                class="w-full bg-brand-600 text-white hover:bg-brand-700"
                :disabled="!file || !password || status === 'loading'"
                @click="unlock"
            >
                {{ t('tools.pdf.ui.unlock_button', 'Unlock PDF') }}
            </Button>

            <StatusMessage :status="status" :message="statusMessage" />
        </div>
    </ToolLayout>
</template>
